/**
 * iter-231-P — Razorpay Route per-vendor diagnostic card.
 *
 * Drops on any admin vendor-detail surface (currently AdminVendorBilling).
 * Shows in one shot:
 *   - Current Razorpay Linked Account id + status (if any)
 *   - The last creation attempt error (if a previous attempt failed) +
 *     raw Razorpay body so admins can see the exact rejection
 *   - Preflight verdict: which fields are missing on the vendor's data
 *     that would block Razorpay v2 /v2/accounts from accepting the call
 *   - Which SSOT collections actually have data (vendor_tax_registration,
 *     vendor_bank_accounts.primary, vendor_pickup_addresses.primary)
 *   - A "Create / Retry" button that hits the existing
 *     POST /api/admin/vendors/{vendor_id}/route-account/create endpoint
 *     and surfaces the full Razorpay error response inline.
 *
 * No mutations on render — safe to embed everywhere.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';
import { Loader2, RefreshCw, AlertCircle, CheckCircle2, PlayCircle, ChevronDown, ChevronUp, Edit, Rocket, Trash2 } from 'lucide-react';
import RazorpayLAFormDialog from './RazorpayLAFormDialog';

const stateBadge = {
  already_created:      { label: 'Already Created', variant: 'default',     color: 'bg-emerald-100 text-emerald-800' },
  ready_never_attempted:{ label: 'Ready · Not Attempted', variant: 'outline', color: 'bg-blue-100 text-blue-800' },
  last_attempt_failed:  { label: 'Last Attempt Failed', variant: 'destructive', color: 'bg-red-100 text-red-800' },
  blocked_missing_fields:{label: 'Blocked · Missing Fields', variant: 'outline', color: 'bg-amber-100 text-amber-800' },
};

// iter-233-C — Per-step result renderer for the activate orchestrator
const ActivateStepRow = ({ label, step, testId }) => {
  if (!step) return null;
  const isOk = step.ok === true;
  const isSkipped = step.action === 'skipped';
  return (
    <li
      className="text-xs flex items-start gap-2"
      data-testid={testId}
    >
      {isOk
        ? <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-600 shrink-0" />
        : isSkipped
          ? <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" />
          : <AlertCircle className="h-3.5 w-3.5 mt-0.5 text-red-600 shrink-0" />}
      <span>
        <span className="font-medium">{label}:</span>{' '}
        <span className={isOk ? 'text-emerald-700' : isSkipped ? 'text-amber-700' : 'text-red-700'}>
          {step.action || (isOk ? 'ok' : 'failed')}
        </span>
        {step.product_id && <code className="ml-1 bg-white px-1 py-0.5 rounded">{step.product_id}</code>}
        {step.stakeholder_id && <code className="ml-1 bg-white px-1 py-0.5 rounded">{step.stakeholder_id}</code>}
        {step.activation_status && <span className="ml-1 text-gray-600">[{step.activation_status}]</span>}
        {step.error && <div className="text-red-700 mt-0.5">{typeof step.error === 'string' ? step.error : JSON.stringify(step.error)}</div>}
        {step.reason && <div className="text-amber-700 mt-0.5">{step.reason}</div>}
        {Array.isArray(step.patched) && step.patched.length > 0 && (
          <div className="text-gray-500 mt-0.5">patched: {step.patched.join(', ')}</div>
        )}
      </span>
    </li>
  );
};

const RazorpayRouteDiagnoseCard = ({ vendorId }) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  // iter-232 Phase 7 — Razorpay-side activation state + requirements[]
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // iter-233-C — Full activation orchestrator (re-run stakeholder + product config)
  const [activating, setActivating] = useState(false);
  const [activateResult, setActivateResult] = useState(null);
  // iter-231-V — modal for the rich create/edit form
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [formDialogMode, setFormDialogMode] = useState('create');
  // iter-234 — Delete Linked Account (so it can be recreated with fixed data)
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    if (!vendorId) return undefined;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const res = await apiCall(`/api/admin/vendors/${vendorId}/route-account/diagnose`);
        if (active) {
          setData(res);
          setCreateResult(null);
        }
      } catch (err) {
        if (active) {
          toast({ title: 'Diagnose failed', description: err.message || 'Could not fetch route diagnosis', variant: 'destructive' });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [vendorId, apiCall, toast]);

  const load = async () => {
    if (!vendorId) return;
    setLoading(true);
    try {
      const res = await apiCall(`/api/admin/vendors/${vendorId}/route-account/diagnose`);
      setData(res);
      setCreateResult(null);
    } catch (err) {
      toast({ title: 'Diagnose failed', description: err.message || 'Could not fetch route diagnosis', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!vendorId) return;
    setCreating(true);
    setCreateResult(null);
    try {
      const res = await apiCall(`/api/admin/vendors/${vendorId}/route-account/create`, { method: 'POST' });
      setCreateResult({ ok: true, body: res });
      toast({ title: 'Linked Account created', description: res.account_id ? `acc_id: ${res.account_id}` : 'See result panel.' });
      await load();
    } catch (err) {
      // apiCall throws on non-2xx — the response body lives on err.detail / err.response if our wrapper preserves it
      let body = err?.response?.detail || err?.detail || null;
      try {
        if (typeof body === 'string') body = JSON.parse(body);
      } catch { /* keep raw string body */ }
      setCreateResult({ ok: false, body: body || { error: err?.message || 'create_failed' } });
      toast({ title: 'Create failed', description: (body?.error || err.message), variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  // iter-232 Phase 7 — Pull live activation status + requirements[] from
  // Razorpay (sync_route_status endpoint, PRD Phase 4). Renders Razorpay's
  // own actionable to-do list so admin can see exactly what's blocking.
  const handleSync = async () => {
    if (!vendorId) return;
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await apiCall(`/api/admin/razorpay/vendors/${vendorId}/route-status/sync`, { method: 'POST' });
      setSyncResult(res);
      toast({
        title: 'Synced from Razorpay',
        description: res?.activation_status
          ? `activation_status: ${res.activation_status}`
          : 'No product config — see panel.',
      });
    } catch (err) {
      setSyncResult({ ok: false, error: err?.message || 'sync_failed' });
      toast({ title: 'Sync failed', description: err?.message || 'Could not sync', variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  // iter-233-C — One-click full activation: re-runs stakeholder create,
  // product config enable/patch, and verifies bank settlements +
  // dashboard_access + refund_back_to_account + tnc_accepted on the
  // live Razorpay product. Renders a per-step checklist.
  const handleActivate = async () => {
    if (!vendorId) return;
    setActivating(true);
    setActivateResult(null);
    try {
      const res = await apiCall(`/api/admin/vendors/${vendorId}/route-account/activate`, { method: 'POST' });
      setActivateResult(res);
      const allOk = res?.ok;
      toast({
        title: allOk ? 'Activation steps complete' : 'Activation finished with issues',
        description: res?.steps?.summary?.activation_status
          ? `activation_status: ${res.steps.summary.activation_status}`
          : 'Review the per-step checklist below.',
        variant: allOk ? 'default' : 'destructive',
      });
      await load();
    } catch (err) {
      let body = err?.response?.detail || err?.detail || null;
      try {
        if (typeof body === 'string') body = JSON.parse(body);
      } catch { /* keep raw */ }
      setActivateResult({ ok: false, error: body?.error || err?.message || 'activation_failed', detail: body });
      toast({ title: 'Activation failed', description: (body?.message || body?.error || err.message), variant: 'destructive' });
    } finally {
      setActivating(false);
    }
  };

  // iter-234 — Delete the Linked Account (hard-delete if pre-activation,
  // suspend+unlink if already activated) so a fresh one can be created
  // with corrected vendor legal/bank details.
  const handleDelete = async () => {
    if (!vendorId) return;
    setDeleting(true);
    try {
      const res = await apiCall(`/api/admin/vendors/${vendorId}/route-account/delete`, { method: 'POST' });
      toast({
        title: res.action === 'deleted' ? 'Linked Account deleted' : 'Linked Account suspended & unlinked',
        description: res.message,
      });
      setCreateResult(null);
      setActivateResult(null);
      setSyncResult(null);
      await load();
    } catch (err) {
      let body = err?.response?.detail || err?.detail || null;
      try {
        if (typeof body === 'string') body = JSON.parse(body);
      } catch { /* keep raw */ }
      toast({ title: 'Delete failed', description: (body?.message || body?.error || err.message), variant: 'destructive' });
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  if (!vendorId) return null;

  const stateInfo = data ? stateBadge[data.current_state] : null;

  return (
    <Card data-testid="razorpay-route-diagnose-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Razorpay Route — Linked Account</CardTitle>
            <CardDescription>
              Diagnose why this vendor doesn&apos;t have a Linked Account, or retry creation with full error visibility.
            </CardDescription>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="route-diagnose-refresh">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            </Button>
            {/* iter-232 Phase 7 — Sync activation status + requirements from Razorpay */}
            {data?.razorpay_linked_account_id && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleSync}
                disabled={syncing}
                data-testid="route-diagnose-sync-razorpay"
                title="Pull live activation status + requirements list from Razorpay"
              >
                {syncing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Sync from Razorpay
              </Button>
            )}
            {/* iter-233-C — One-click full activation (stakeholder + product config) */}
            {data?.razorpay_linked_account_id && (
              <Button
                variant="default"
                size="sm"
                onClick={handleActivate}
                disabled={activating}
                data-testid="route-diagnose-activate-la"
                title="Re-run Stakeholder + Product Config (bank, dashboard, returns) on the existing LA"
              >
                {activating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Rocket className="h-4 w-4 mr-2" />}
                Activate LA
              </Button>
            )}
            {/* iter-231-V — Edit existing LA (visible only when account exists) */}
            {data?.razorpay_linked_account_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setFormDialogMode('edit'); setFormDialogOpen(true); }}
                data-testid="route-diagnose-edit-form"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Linked Account
              </Button>
            )}
            {/* iter-234 — Delete Linked Account so it can be recreated
                with corrected data (e.g. failed verification). */}
            {data?.razorpay_linked_account_id && (
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={deleting}
                data-testid="route-diagnose-delete-la"
                title="Delete this Linked Account from Razorpay so a new one can be created with corrected details"
              >
                {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete Linked Account
              </Button>
            )}
            {/* iter-231-V — Open the rich Create form (autofilled,
                inline validation, full Razorpay error visibility) */}
            {!data?.razorpay_linked_account_id && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => { setFormDialogMode('create'); setFormDialogOpen(true); }}
                data-testid="route-diagnose-open-create-form"
              >
                <Edit className="h-4 w-4 mr-2" />
                Open Create Form
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleCreate}
              disabled={creating || data?.current_state === 'already_created'}
              data-testid="route-diagnose-create"
            >
              {creating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <PlayCircle className="h-4 w-4 mr-2" />}
              {data?.current_state === 'already_created'
                ? 'Already Linked'
                : data?.razorpay_linked_account_last_error ? 'Retry Create' : 'Create Linked Account'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading && !data && (
          <p className="text-sm text-gray-500">Loading diagnosis…</p>
        )}

        {data && (
          <>
            {/* State pill + headline */}
            <div className="flex items-center gap-3">
              {stateInfo && (
                <Badge className={stateInfo.color} variant="outline" data-testid="route-diagnose-state">
                  {stateInfo.label}
                </Badge>
              )}
              {data.razorpay_linked_account_id && (
                <code className="text-xs bg-slate-100 px-2 py-1 rounded" data-testid="route-diagnose-account-id">
                  {data.razorpay_linked_account_id}
                </code>
              )}
              {data.razorpay_linked_account_status && (
                <span className="text-xs text-gray-500">status: {data.razorpay_linked_account_status}</span>
              )}
            </div>

            {/* Next action banner */}
            {data.next_action && (
              <Alert variant={data.current_state === 'last_attempt_failed' ? 'destructive' : 'default'}>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Next action</AlertTitle>
                <AlertDescription className="text-sm">{data.next_action}</AlertDescription>
              </Alert>
            )}

            {/* Preflight grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="rounded border p-3 bg-slate-50">
                <p className="font-medium text-xs uppercase text-gray-500 mb-2">Preflight</p>
                <p>OK: {data.preflight?.ok ? <CheckCircle2 className="inline h-4 w-4 text-emerald-600" /> : <AlertCircle className="inline h-4 w-4 text-red-600" />}</p>
                <p>Has PAN: {data.preflight?.has_pan ? '✓' : '—'}</p>
                <p>Has GST: {data.preflight?.has_gst ? '✓' : '—'}</p>
                <p>Has Bank: {data.preflight?.has_bank ? '✓' : '—'}</p>
                {data.preflight?.derived_business_type && (
                  <p>Business type: <code className="text-xs">{data.preflight.derived_business_type}</code></p>
                )}
                {Array.isArray(data.preflight?.missing) && data.preflight.missing.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-red-700 font-medium">Missing hard fields:</p>
                    <ul className="list-disc list-inside text-xs text-red-700">
                      {data.preflight.missing.map((m) => (<li key={m}>{m}</li>))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="rounded border p-3 bg-slate-50">
                <p className="font-medium text-xs uppercase text-gray-500 mb-2">SSOT data sources</p>
                <p>vendor_tax_registration: {data.data_sources?.vendor_tax_registration ? '✓' : '—'}</p>
                <p>vendor_bank_accounts (primary): {data.data_sources?.vendor_bank_accounts_primary ? '✓' : '—'}</p>
                <p>vendor_pickup_addresses (primary): {data.data_sources?.vendor_pickup_addresses_primary ? '✓' : '—'}</p>
              </div>
            </div>

            {/* iter-232 Phase 7 — Razorpay's live `requirements[]` checklist.
                Renders the actionable to-do list Razorpay returns on the
                Product Config entity (Doc #8). Each item links its
                resolution_url so admin can see exactly what to PATCH. */}
            {syncResult && (
              <div className="rounded border p-3 bg-blue-50 border-blue-200" data-testid="route-diagnose-razorpay-sync-panel">
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-medium text-xs uppercase text-blue-900">Razorpay live status</p>
                  {syncResult.activation_status && (
                    <Badge
                      className={
                        syncResult.activation_status === 'activated'
                          ? 'bg-emerald-100 text-emerald-800'
                          : syncResult.activation_status === 'under_review'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                      }
                      variant="outline"
                      data-testid="route-diagnose-activation-status"
                    >
                      {syncResult.activation_status}
                    </Badge>
                  )}
                  {syncResult.tnc_accepted === true && (
                    <span className="text-xs text-emerald-700">T&amp;C accepted ✓</span>
                  )}
                </div>
                {Array.isArray(syncResult.requirements) && syncResult.requirements.length > 0 ? (
                  <div>
                    <p className="text-xs text-blue-900 font-medium mb-1">
                      Razorpay needs the following before activation:
                    </p>
                    <ul className="space-y-1" data-testid="route-diagnose-requirements-list">
                      {syncResult.requirements.map((r, idx) => (
                        <li
                          key={`${r.field_reference || 'req'}-${idx}`}
                          className="text-xs flex items-start gap-2"
                          data-testid={`route-diagnose-req-${idx}`}
                        >
                          <AlertCircle className="h-3 w-3 mt-0.5 text-amber-600 shrink-0" />
                          <span>
                            <code className="bg-white px-1 py-0.5 rounded">{r.field_reference}</code>
                            {' — '}
                            <span className="text-gray-700">{r.reason_code}</span>
                            {r.status && r.status !== 'required' && (
                              <span className="text-gray-500"> ({r.status})</span>
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : syncResult.activation_status === 'activated' ? (
                  <p className="text-xs text-emerald-700">
                    <CheckCircle2 className="inline h-3 w-3 mr-1" />
                    No outstanding requirements — vendor can accept payouts.
                  </p>
                ) : !syncResult.ok && syncResult.error ? (
                  <p className="text-xs text-red-700">Sync error: {String(syncResult.error)}</p>
                ) : (
                  <p className="text-xs text-gray-600">
                    No requirements returned. {syncResult.product_id
                      ? `(product: ${syncResult.product_id})`
                      : '(no product config yet — create the Linked Account first)'}
                  </p>
                )}
              </div>
            )}

            {/* iter-233-C — Per-step checklist from the activation orchestrator */}
            {activateResult && (
              <div
                className={`rounded border p-3 ${
                  activateResult.ok
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-amber-50 border-amber-200'
                }`}
                data-testid="route-diagnose-activate-panel"
              >
                <div className="flex items-center gap-2 mb-2">
                  <p className="font-medium text-xs uppercase text-gray-700">
                    Activation result
                  </p>
                  {activateResult.steps?.summary?.activation_status && (
                    <Badge
                      className={
                        activateResult.steps.summary.activation_status === 'activated'
                          ? 'bg-emerald-100 text-emerald-800'
                          : 'bg-amber-100 text-amber-800'
                      }
                      variant="outline"
                      data-testid="route-diagnose-activate-status"
                    >
                      {activateResult.steps.summary.activation_status}
                    </Badge>
                  )}
                </div>
                {activateResult.steps ? (
                  <>
                    <ul className="space-y-1.5" data-testid="route-diagnose-activate-steps">
                      <ActivateStepRow
                        label="LA fetch"
                        step={activateResult.steps.fetch}
                        testId="route-diagnose-activate-step-fetch"
                      />
                      <ActivateStepRow
                        label="Stakeholder"
                        step={activateResult.steps.stakeholder}
                        testId="route-diagnose-activate-step-stakeholder"
                      />
                      <ActivateStepRow
                        label="Product Config"
                        step={activateResult.steps.product}
                        testId="route-diagnose-activate-step-product"
                      />
                    </ul>
                    {activateResult.steps.summary && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs font-medium text-gray-700 mb-1.5">Live config check</p>
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                          <div data-testid="activate-summary-bank">
                            {activateResult.steps.summary.bank_settlement_set
                              ? <CheckCircle2 className="inline h-3 w-3 text-emerald-600 mr-1" />
                              : <AlertCircle className="inline h-3 w-3 text-red-600 mr-1" />}
                            Bank settlement
                          </div>
                          <div data-testid="activate-summary-tnc">
                            {activateResult.steps.summary.tnc_accepted
                              ? <CheckCircle2 className="inline h-3 w-3 text-emerald-600 mr-1" />
                              : <AlertCircle className="inline h-3 w-3 text-red-600 mr-1" />}
                            T&amp;C accepted
                          </div>
                          <div data-testid="activate-summary-stakeholder">
                            {activateResult.steps.summary.stakeholder_present
                              ? <CheckCircle2 className="inline h-3 w-3 text-emerald-600 mr-1" />
                              : <AlertCircle className="inline h-3 w-3 text-red-600 mr-1" />}
                            Stakeholder present
                          </div>
                          <div data-testid="activate-summary-notifications">
                            {activateResult.steps.summary.notifications_configured
                              ? <CheckCircle2 className="inline h-3 w-3 text-emerald-600 mr-1" />
                              : <AlertCircle className="inline h-3 w-3 text-gray-400 mr-1" />}
                            Notifications
                          </div>
                          <div data-testid="activate-summary-refund-policy">
                            {activateResult.steps.summary.refund_policy_configured
                              ? <CheckCircle2 className="inline h-3 w-3 text-emerald-600 mr-1" />
                              : <AlertCircle className="inline h-3 w-3 text-gray-400 mr-1" />}
                            Refund policy
                          </div>
                        </div>
                        <p className="text-[10px] text-gray-500 mt-2 italic">
                          Dashboard Access and Customer Returns are merchant-level Razorpay Dashboard settings — not configurable via per-LA API. Enable them once on
                          {' '}<a href="https://dashboard.razorpay.com" target="_blank" rel="noreferrer" className="underline">dashboard.razorpay.com → Settings → Configuration</a>.
                        </p>
                        {Array.isArray(activateResult.steps.summary.pending_fields)
                          && activateResult.steps.summary.pending_fields.length > 0 && (
                          <div className="mt-2">
                            <p className="text-xs text-amber-700 font-medium">Pending:</p>
                            <ul className="list-disc list-inside text-xs text-amber-700">
                              {activateResult.steps.summary.pending_fields.map((f) => (
                                <li key={f}>{f}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-red-700">
                    {activateResult.error || activateResult.message || 'Activation failed'}
                  </p>
                )}
              </div>
            )}

            {/* Last attempt error.
            {data.last_attempt_error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>Last attempt error</AlertTitle>
                <AlertDescription>
                  <p className="font-mono text-xs">
                    {typeof data.last_attempt_error === 'string'
                      ? data.last_attempt_error
                      : (data.last_attempt_error?.description
                          || data.last_attempt_error?.error
                          || data.last_attempt_error?.code
                          || JSON.stringify(data.last_attempt_error))}
                  </p>
                  {data.last_attempt_at && <p className="text-xs mt-1">at: {data.last_attempt_at}</p>}
                  {data.last_attempt_raw && (
                    <>
                      <button
                        type="button"
                        className="text-xs underline mt-2 inline-flex items-center gap-1"
                        onClick={() => setShowRaw((v) => !v)}
                        data-testid="route-diagnose-toggle-raw"
                      >
                        {showRaw ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                        {showRaw ? 'Hide' : 'Show'} raw Razorpay body
                      </button>
                      {showRaw && (
                        <pre className="text-xs bg-white text-gray-800 p-2 mt-2 rounded overflow-auto max-h-48" data-testid="route-diagnose-raw-body">
{typeof data.last_attempt_raw === 'string' ? data.last_attempt_raw : JSON.stringify(data.last_attempt_raw, null, 2)}
                        </pre>
                      )}
                    </>
                  )}
                </AlertDescription>
              </Alert>
            )}

            {/* Create-attempt result (this session) */}
            {createResult && (
              <Alert variant={createResult.ok ? 'default' : 'destructive'} data-testid="route-diagnose-create-result">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{createResult.ok ? 'Create response' : 'Create failed'}</AlertTitle>
                <AlertDescription>
                  <pre className="text-xs bg-white text-gray-800 p-2 rounded overflow-auto max-h-64">
{JSON.stringify(createResult.body, null, 2)}
                  </pre>
                </AlertDescription>
              </Alert>
            )}
          </>
        )}
      </CardContent>

      {/* iter-231-V — Rich create / edit form modal */}
      <RazorpayLAFormDialog
        vendorId={vendorId}
        mode={formDialogMode}
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        onSuccess={() => { setFormDialogOpen(false); load(); }}
      />

      {/* iter-234 — Delete confirmation */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent data-testid="route-diagnose-delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Linked Account?</AlertDialogTitle>
            <AlertDialogDescription>
              If Razorpay hasn&apos;t activated it yet (typical for a failed
              verification), it will be permanently deleted and you can
              immediately create a new one with corrected details. If it
              was already activated, Razorpay doesn&apos;t allow hard
              deletion — it will be suspended and unlinked from this
              vendor instead, and the old account stays visible
              (suspended) on Razorpay&apos;s dashboard. This does not
              change the vendor&apos;s bank/tax/address records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="route-diagnose-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="route-diagnose-delete-confirm"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete Linked Account
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default RazorpayRouteDiagnoseCard;
